/**
 * Push Notifications via Capacitor + Firebase Cloud Messaging
 * Handles permission request, token registration, and incoming notifications
 */
import { Capacitor } from '@capacitor/core';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

let initialized = false;

/**
 * Initialize push notifications on native platforms.
 * Call after login with the customer's portal token and ID.
 */
export async function initPushNotifications(portalToken, customerId) {
  if (!Capacitor.isNativePlatform() || initialized) return;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Check / request permission
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      console.warn('Push notifications permission not granted');
      return;
    }

    // Register with APNs / FCM
    await PushNotifications.register();

    // Listen for the FCM token
    PushNotifications.addListener('registration', async (token) => {
      console.log('Push token received:', token.value?.substring(0, 20) + '...');
      try {
        await fetch(`${API}/admin/firebase/register-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${portalToken}`,
          },
          body: JSON.stringify({
            customer_id: customerId,
            fcm_token: token.value,
            device_type: Capacitor.getPlatform(), // 'ios' or 'android'
          }),
        });
      } catch (err) {
        console.error('Failed to register push token:', err);
      }
    });

    // Handle registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    // Handle incoming notifications while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push received in foreground:', notification.title);
      // Show an in-app toast or banner
      if (window.__showPushToast) {
        window.__showPushToast(notification.title, notification.body);
      }
    });

    // Handle notification tap (app opened from notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data;
      console.log('Push action:', data);
      // Navigate based on notification data
      if (data?.screen && window.__navigateMobile) {
        window.__navigateMobile(data.screen);
      }
    });

    initialized = true;
  } catch (err) {
    console.error('Push notifications init error:', err);
  }
}

/**
 * Remove push token on logout
 */
export async function removePushToken(customerId) {
  if (!Capacitor.isNativePlatform()) return;
  
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllListeners();
    initialized = false;

    await fetch(`${API}/admin/firebase/token/${customerId}`, {
      method: 'DELETE',
    });
  } catch {
    // Best effort cleanup
  }
}
